import { useCallback, useEffect, useState, lazy, Suspense } from 'react';
import { restoreSession, signOut, type AuthUser } from './lib/firebase.js';
import { useGame } from './lib/useGame.js';
import { useRouter, type Route } from './lib/useRouter.js';
import { AuthScreen } from './screens/AuthScreen.js';
import { CountdownScreen } from './screens/CountdownScreen.js';
import { DisconnectOverlay, OpponentDisconnectedBanner } from './screens/DisconnectOverlay.js';
import { HomeScreen } from './screens/HomeScreen.js';
import { RaceScreen } from './screens/RaceScreen.js';
import { ResultScreen } from './screens/ResultScreen.js';
import { RoomScreen } from './screens/RoomScreen.js';
import { RulesScreen } from './screens/RulesScreen.js';
import { StatsScreen } from './screens/StatsScreen.js';
import { SearchScreen } from './screens/SearchScreen.js';
import { NicknameOverlay } from './screens/NicknameOverlay.js';
import { color } from './theme.js';

// Dev-only Game LAB, code-split into its own chunk. It imports the server engine
// classes directly (aliased to a browser RNG by Vite), so it plays any game solo
// — no matchmaking, no socket, no server.
//
// It reaches into `apps/server` source, which is NOT present in the production
// client image (that build ships only apps/client + packages/contracts). So the
// glob is guarded by `import.meta.env.DEV`: in a production build the condition
// is statically false, the branch is tree-shaken, and Vite never tries to bundle
// the Lab (or resolve its server-source imports). `dev/` is excluded from tsc
// entirely; Vite is the only thing that compiles it.
const labModules = import.meta.env.DEV ? import.meta.glob('./dev/LabScreen.tsx') : {};
const LabScreen = lazy(async () => {
  const load = labModules['./dev/LabScreen.tsx'];
  if (!load) return { default: (() => null) as React.ComponentType<{ onBack: () => void }> };
  const m = (await load()) as { LabScreen: React.ComponentType<{ onBack: () => void }> };
  return { default: m.LabScreen };
});

export function App() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [restoring, setRestoring] = useState(true);

  useEffect(() => {
    void restoreSession()
      .then(setUser)
      .finally(() => setRestoring(false));
  }, []);

  if (restoring) {
    return (
      <div style={{ height: '100%', display: 'grid', placeItems: 'center', background: color.bg, color: color.textFaint, fontSize: 14, letterSpacing: '.2em' }}>
        LOADING…
      </div>
    );
  }

  if (!user) return <AuthScreen onAuth={setUser} />;

  return <Game user={user} onSignOut={() => void signOut().then(() => setUser(null))} />;
}

function Game({ user, onSignOut }: { user: AuthUser; onSignOut: () => void }) {
  const { state, actions, remainingMs, elapsedMs, refreshProfile, fetchStats, saveNickname } = useGame(user);

  // URL ↔ screen routing. Maps the current route (deep link / back / forward)
  // onto the right action; /room/CODE is the invite link.
  const handleRoute = useCallback(
    (route: Route, initial: boolean) => {
      switch (route.name) {
        case 'room':
          actions.joinRoom(route.code);
          break;
        case 'play':
          actions.joinQueue();
          break;
        case 'stats':
          actions.showStats();
          break;
        case 'how':
          actions.showRules();
          break;
        case 'lab':
          actions.showLab();
          break;
        case 'home':
          // On the very first load, "/" needs no action (we're already home).
          // On a back-navigation to "/", return home from wherever we are.
          if (!initial) actions.goHome();
          break;
      }
    },
    [actions],
  );
  useRouter(state.screen, state.roomCode, handleRoute);

  // Offer a rematch vs the same opponent, or withdraw if already waiting.
  const rematch = useCallback(() => {
    const matchId = state.result?.matchId;
    if (!matchId) return;
    if (state.rematch?.youOffered) actions.cancelRematch(matchId);
    else actions.offerRematch(matchId);
  }, [actions, state.result?.matchId, state.rematch?.youOffered]);

  // Withdraw any pending rematch offer when leaving the result screen, so the
  // opponent stops seeing "waiting".
  const withdrawRematch = useCallback(() => {
    const matchId = state.result?.matchId;
    if (matchId && state.rematch?.youOffered) actions.cancelRematch(matchId);
  }, [actions, state.result?.matchId, state.rematch?.youOffered]);

  // Fall back to matchmaking for a fresh opponent.
  const newMatch = useCallback(() => {
    withdrawRematch();
    refreshProfile();
    actions.backToLobby();
    actions.joinQueue();
  }, [actions, refreshProfile, withdrawRematch]);

  const toLobby = useCallback(() => {
    withdrawRematch();
    refreshProfile();
    actions.backToLobby();
  }, [actions, refreshProfile, withdrawRematch]);

  const inRace = state.screen === 'race';

  /**
   * Every screen's render is guarded on the data it needs (`state.match`,
   * `state.result`, ...). If the screen and its data ever disagree — as they did
   * when a reconnect set screen='countdown' without sending match:found — every
   * branch is skipped and the page goes black with no way out. Detect that and
   * render a recovery screen rather than nothing.
   */
  const willRenderSomething =
    state.screen === 'home' ||
    state.screen === 'rules' ||
    state.screen === 'stats' ||
    state.screen === 'lab' ||
    state.screen === 'searching' ||
    (state.screen === 'room' && state.roomCode !== null) ||
    (state.screen === 'join-room' && state.roomCode !== null) ||
    (state.screen === 'countdown' && state.match !== null && state.countdownStartsAt !== null) ||
    (state.screen === 'race' && state.race !== null) ||
    (state.screen === 'result' && state.result !== null && state.profile !== null);

  if (!willRenderSomething) {
    return <Recovering screen={state.screen} onLobby={actions.abandonMatch} />;
  }

  return (
    <>
      {state.screen === 'home' && (
        <HomeScreen
          profile={state.profile}
          onFindMatch={actions.joinQueue}
          onCreateRoom={actions.createRoom}
          onJoinRoom={actions.joinRoom}
          onSignOut={onSignOut}
          onHowItWorks={actions.showRules}
          onShowStats={actions.showStats}
          onOpenLab={actions.showLab}
        />
      )}

      {state.screen === 'rules' && <RulesScreen onBack={actions.hideRules} />}
      {state.screen === 'stats' && <StatsScreen onBack={actions.hideStats} fetchStats={fetchStats} />}
      {state.screen === 'lab' && (
        <Suspense fallback={null}>
          <LabScreen onBack={actions.hideLab} />
        </Suspense>
      )}

      {state.screen === 'searching' && (
        <SearchScreen profile={state.profile} playersInQueue={state.playersInQueue} onCancel={actions.leaveQueue} />
      )}

      {state.screen === 'room' && state.roomCode && (
        <RoomScreen code={state.roomCode} profile={state.profile} onCancel={actions.cancelRoom} />
      )}

      {state.screen === 'join-room' && state.roomCode && (
        <JoiningRoom code={state.roomCode} onCancel={actions.goHome} />
      )}

      {state.screen === 'countdown' && state.match && state.countdownStartsAt !== null && (
        <CountdownScreen match={state.match} startsAt={state.countdownStartsAt} />
      )}

      {inRace && state.race && (
        <RaceScreen state={state} remainingMs={remainingMs} sendEvent={actions.sendEvent} sendReaction={actions.sendReaction} elapsedMs={elapsedMs} />
      )}

      {state.screen === 'result' && state.result && state.profile && (
        <ResultScreen result={state.result} youId={state.profile.id} rematch={state.rematch} onRematch={rematch} onNewMatch={newMatch} onLobby={toLobby} />
      )}

      {/* Overlays */}
      {inRace && state.disconnected && <DisconnectOverlay remainingMs={remainingMs} />}
      {inRace && state.opponentDisconnected && !state.disconnected && <OpponentDisconnectedBanner />}

      {/*
        First sign-in for a signed-up user: pick a nickname before anything else.
        Sits above every screen and blocks interaction until saved. Guests never
        see it (needsNickname is always false for them).
      */}
      {state.profile?.needsNickname && (
        <NicknameOverlay suggestion={user.displayName} onSave={saveNickname} />
      )}

      {state.error && <Toast message={state.error} onDismiss={actions.dismissError} />}
    </>
  );
}

/**
 * The joiner's view after following a /room/CODE invite: a brief "joining…"
 * state while we reach the host. matchFound moves us into the countdown; a room
 * error bounces back home (handled in useGame).
 */
function JoiningRoom({ code, onCancel }: { code: string; onCancel: () => void }) {
  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 22,
        background: color.bg,
        padding: 24,
        textAlign: 'center',
      }}
    >
      <span style={{ fontSize: 12, letterSpacing: '.34em', color: 'rgba(166,255,46,.6)', fontWeight: 600 }}>JOINING FRIEND MATCH</span>
      <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, fontSize: 'clamp(28px,7vw,48px)', letterSpacing: '.14em', color: color.lime, textShadow: '0 0 16px rgba(166,255,46,.5)' }}>
        {code}
      </span>
      <div style={{ width: 44, height: 44, borderRadius: '50%', border: '3px solid rgba(166,255,46,.2)', borderTopColor: color.lime, animation: 'dcSpin 0.9s linear infinite' }} />
      <span style={{ fontSize: 13, color: 'rgba(234,255,234,.5)' }}>Connecting to the host…</span>
      <button
        type="button"
        onClick={onCancel}
        style={{ marginTop: 6, padding: '11px 26px', borderRadius: 999, border: '1px solid rgba(255,46,77,.3)', background: 'rgba(255,46,77,.06)', color: color.redBright, fontWeight: 700, fontSize: 13, letterSpacing: '.04em', cursor: 'pointer' }}
      >
        CANCEL
      </button>
    </div>
  );
}

/**
 * Shown when the app reaches a state no screen can render — a stale match on the
 * server, a dropped payload, a bug. Always offers a way back to the lobby, so a
 * black screen is never a dead end.
 */
function Recovering({ screen, onLobby }: { screen: string; onLobby: () => void }) {
  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 18,
        background: color.bg,
        fontFamily: "'Chakra Petch',system-ui,sans-serif",
        padding: 24,
        textAlign: 'center',
      }}
    >
      <span style={{ fontSize: 12, letterSpacing: '.3em', color: color.amber, fontWeight: 600 }}>
        SYNCING WITH SERVER
      </span>
      <span style={{ fontSize: 22, fontWeight: 700, color: color.text, letterSpacing: '.02em' }}>
        Restoring your match…
      </span>
      <span style={{ fontSize: 13, color: 'rgba(234,255,234,.45)', maxWidth: 380, lineHeight: 1.5 }}>
        If this doesn&apos;t clear in a moment, the match has probably ended.
        <br />
        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, opacity: 0.6 }}>
          (state: {screen})
        </span>
      </span>
      <button
        onClick={onLobby}
        style={{
          marginTop: 8,
          padding: '13px 28px',
          border: '1px solid rgba(166,255,46,.3)',
          borderRadius: 12,
          background: 'rgba(166,255,46,.06)',
          color: color.limeBright,
          fontWeight: 700,
          fontSize: 15,
          letterSpacing: '.04em',
        }}
      >
        BACK TO LOBBY
      </button>
    </div>
  );
}

function Toast({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <button
      onClick={onDismiss}
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '12px 20px',
        border: '1px solid rgba(255,46,77,.4)',
        borderRadius: 12,
        background: 'linear-gradient(180deg,rgba(40,7,13,.96),rgba(28,5,9,.96))',
        color: color.redBright,
        fontWeight: 600,
        fontSize: 14,
        fontFamily: "'Chakra Petch',system-ui,sans-serif",
        boxShadow: '0 10px 30px rgba(0,0,0,.5)',
      }}
    >
      {message}
    </button>
  );
}
